import { Injectable, Injector, effect, inject } from '@angular/core';
import { OfflineOrderService } from './offline-order.service';
import { NetworkStatusService } from './network-status.service';
import { TerminalIdentity } from './terminal-identity';
import { db } from './db';
import { negocioDeLaSesionAbierta } from '../tenant/negocio-del-token';
import { SessionService } from '../tenant/session.service';
import { EmparejarCajaService } from '../../services/emparejar-caja.service';

/**
 * Drena el outbox de órdenes en segundo plano (F2): cada `intervalMs` y cada vez
 * que el navegador recupera red (`online`). Espejo del SyncOutboxScheduler de
 * ms-order-product (Java), pero en el cliente. Se arranca una vez en el bootstrap.
 * Ver docs/140 / docs/30-offline-y-sync.md.
 */
@Injectable({ providedIn: 'root' })
export class SyncScheduler {
  private offlineOrders = inject(OfflineOrderService);
  private network = inject(NetworkStatusService);
  private session = inject(SessionService);
  private injector = inject(Injector);
  private handle: ReturnType<typeof setInterval> | null = null;
  private running = false;
  /**
   * M26 (04/10): las ventas FAILED solo se reintentaban con el evento `online`. Con la red siempre arriba ese evento
   * no llega, y una venta caída por un 5xx agotado o un 4xx pasajero quedaba atascada. Ahora también se reintentan
   * al arrancar, al entrar con una sesión y cada `reintentoDeFallidasMs` (lento: un rechazo no se martillea cada 7 s).
   * Los rechazos definitivos no se reencolan (`requeueFailed`), y el servidor dedupea por idempotencyKey.
   */
  private reintentoDeFallidasMs = 5 * 60_000;
  private ultimoReintentoDeFallidas = 0;

  constructor() {
    // Al entrar con una cuenta (o cambiar de negocio) se reintenta lo que quedó FAILED de esa cuenta.
    let anterior: string | null | undefined;
    effect(() => {
      const negocio = this.session.session()?.tenantId ?? null;
      if (anterior !== undefined && negocio && negocio !== anterior) {
        void this.tick(true);
        this.revisarEmparejamiento();
      }
      anterior = negocio;
    });
  }

  start(intervalMs = 7000, reintentoDeFallidasMs = 5 * 60_000): void {
    if (this.handle) return; // ya arrancado
    this.reintentoDeFallidasMs = reintentoDeFallidasMs;
    this.handle = setInterval(
      () => this.tick(Date.now() - this.ultimoReintentoDeFallidas >= this.reintentoDeFallidasMs), intervalMs);
    if (typeof window !== 'undefined') {
      // Al volver la red: RESINCRONIZACIÓN AUTOMÁTICA (N2/6.2). Se reencola
      // también lo que quedó RECHAZADO, para que el banner rojo no se quede
      // pegado pidiendo una acción que el usuario no puede hacer.
      window.addEventListener('online', () => { void this.tick(true); this.revisarEmparejamiento(); });
    }
    // V35 — La identidad del terminal se asegura ANTES de nada. Si es el primer
    // arranque genera su UUID; si detecta que perdió el contador de secuencia
    // pero conserva el UUID, sube el `epoch` para que el reinicio quede
    // explicado en vez de parecer un ataque de repetición.
    //
    // Va antes del rescate y del drenado a propósito: cualquier evento que se
    // encole después tiene que salir ya con su posición en la cadena.
    //
    // F2.1: la identidad es del negocio de la sesión abierta. Sin sesión no hay
    // negocio al que dársela; la primera venta de cada negocio la crea igual.
    const negocio = negocioDeLaSesionAbierta();
    (negocio ? new TerminalIdentity(db).inicializar(negocio) : Promise.resolve(''))
      .catch(() => undefined) // sin identidad se vende igual; el servidor la dará de alta luego
      // Rescata eventos reclamados por una sesión anterior que murió a mitad del
      // POST; recién después se drena (si no, quedarían atascados para siempre).
      .then(() => this.offlineOrders.recoverStaleClaims())
      .catch(() => 0)
      .then(() => this.tick(true)) // intento inicial: también lo FAILED de antes de cerrar la app (M26)
      .then(() => this.revisarEmparejamiento());
  }

  stop(): void {
    if (this.handle) {
      clearInterval(this.handle);
      this.handle = null;
    }
  }

  /**
   * Una pasada de drenado, reentrante-segura (no solapa).
   * @param reintentarRechazadas reencola los FAILED antes de drenar (al reconectar).
   */
  private async tick(reintentarRechazadas = false): Promise<void> {
    if (this.running) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      await this.network.refresh(); // aunque no sincronice, refresca el contador
      return;
    }
    this.running = true;
    if (reintentarRechazadas) this.ultimoReintentoDeFallidas = Date.now();
    try {
      const r = await (reintentarRechazadas
        ? this.offlineOrders.retryFailed()
        : this.offlineOrders.syncPending());
      // M26 (2): tras un pase que subió ventas, ¿la caja sigue donde debe? (el servicio lee como mucho cada 5 min).
      if ((r?.synced ?? 0) > 0) this.revisarEmparejamiento();
    } catch {
      /* se reintenta en el próximo tick */
    } finally {
      this.running = false;
      await this.network.refresh();
    }
  }

  /**
   * M26 (2): pide al servicio de emparejar que mire el estado de la caja. Se resuelve aquí, al usarlo, y no en el
   * constructor: el servicio arrastra TableService y no debe entrar en el grafo del arranque. Nunca lanza.
   */
  private revisarEmparejamiento(): void {
    try {
      void this.injector.get(EmparejarCajaService).revisarEmparejamiento();
    } catch { /* sin servicio (pruebas) o sin sesión: nada que mirar */ }
  }
}
